/**
 * Hook React Query pour les factures
 * Remplace useInvoices avec refetch automatique
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { isFeatureEnabled } from '@/config/features';
import { useAuth } from '@/hooks/useAuth';

export interface Invoice {
  id: string;
  stripe_id: string;
  description: string;
  date: string;
  amount: number;
  currency: string;
  status: 'paid' | 'pending' | 'cancelled' | 'failed' | 'unknown';
  pdf_url?: string;
  hosted_url?: string;
  payment_intent_id?: string;
}

/**
 * Hook pour charger les factures avec React Query
 */
export function useInvoicesQuery(organizationId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['invoices', organizationId],
    
    queryFn: async (): Promise<Invoice[]> => {
      if (!organizationId) {
        return [];
      }

      const { data, error } = await supabase.functions.invoke('get-invoices', {
        body: {
          organization_id: organizationId,
          limit: 50
        }
      });

      if (error) {
        throw new Error(error.message || 'Erreur lors de la récupération des factures');
      }

      return data.invoices || [];
    },
    
    enabled: !!organizationId,
    
    // Refetch automatique (contrôlé par feature flag)
    refetchOnWindowFocus: isFeatureEnabled('REACT_QUERY_REFETCH', user?.id),
    
    // Cache plus court pour les factures (elles changent rarement)
    staleTime: 1000 * 60, // 1 minute
    
    // Retry en cas d'erreur
    retry: 2,
  });
}
